import { v4 as uuid } from 'uuid';
import { GlobalContextState } from '../global-context-state';
import { EventName } from '../event-name';

export class BaseEvent {
  // Final payload to be sent down to kafka, need to abstract this out
  // as we can't just serialize this object due to JS marking everything
  // as public when TS transpiles down
  public Data: any;

  // name of the event to be used in kafka
  public Name: EventName;

  // debug is used to prevent the event from actually sending but to send to console
  // when a query param is provided in order to trace out for production only metadata
  public Debug = false;

  // generated client side as a guid for this specific instance of the event
  public EventId: string;

  // the unique id this generates that gets added as context to other events
  protected ContextPropertyName: string = null;

  // every event will probably end up wanting this
  private RequiredContext: string[] = ['AppInstanceId'];

  // do not fire a referral before it gets all required events to fire first so we can
  // manage the async.  e.g. referral has to fire after pageview but we don't want to manage
  // async all over the place, the consumer of this lib can just configure what an event needs
  protected EventSpecificRequiredContext: string[] = [];

  // there may be other context that are global but aren't required so these will be non blocking
  // and will just be included into the payload if it's set
  protected EventSpecificOptionalContext: string[] = [];

  // timestamp when this event is constructed, utc string
  protected ClientEventTime: string;

  constructor(eventId: string = null) {
    // if no id was provided for this event, then we'll generate a new guid for it.  some events this id is generated
    // externally like with session so this is to provide that id back in so this eventId will match for joining to
    // other events
    this.EventId = eventId ? eventId : uuid();
    this.ClientEventTime = new Date().toUTCString();
  }

  private setGlobalContext(context: any): object {
    const contextToMerge = [].concat(
      this.RequiredContext,
      this.EventSpecificRequiredContext,
      this.EventSpecificOptionalContext,
    );

    // return a new object with only the contexts we care about
    return Object.keys(context)
      .filter((key) => contextToMerge.includes(key))
      .reduce((obj: any, key) => {
        obj[key] = context[key];
        return obj;
      }, {});
  }

  public getContext(): any {
    // check if this event provides global context
    if (!this.ContextPropertyName) {
      return;
    }

    return {
      [this.ContextPropertyName]: this.EventId,
    };
  }

  // serialize only the public properties and remove all nulls, double check if we want to do this
  // in the UI or the API?
  public toPayload(context: GlobalContextState): any {
    // merge in the global context that this event requires before returning the final object
    return Object.assign(this.Data, this.setGlobalContext(context), {
      Name: this.Name,
      EventId: this.EventId,
      ClientEventTime: this.ClientEventTime,
    });
  }

  public getRequiredContext(): string[] {
    return this.RequiredContext.concat(this.EventSpecificRequiredContext);
  }
}
